import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users2, Plus, KeyRound, Trash2, Pencil } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ROLE_LABELS, ROLE_OPTIONS, TYP_OPTIONS, STAFF_ROLES } from "@/lib/constants";

const emptyCreate = { titel: "", vorname: "", nachname: "", geschlecht: "", typ: "", email: "", password: "", roles: ["user-katechet"] };

function TypField({ geschlecht, value, onChange, idPrefix }) {
  const options = TYP_OPTIONS[geschlecht] || [];
  const isPreset = options.includes(value);
  const sel = value === "" ? "" : isPreset ? value : "__sonstiges";
  return (
    <div className="space-y-2">
      <Label>Typ</Label>
      <Select value={sel} onValueChange={(v) => onChange(v === "__sonstiges" ? " " : v)}>
        <SelectTrigger data-testid={`${idPrefix}-typ`}>
          <SelectValue placeholder={geschlecht ? "Bitte wählen" : "Zuerst Geschlecht wählen"} />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          <SelectItem value="__sonstiges">Sonstiges…</SelectItem>
        </SelectContent>
      </Select>
      {sel === "__sonstiges" && (
        <Input value={value.trim()} onChange={(e) => onChange(e.target.value)} placeholder="Bitte angeben" data-testid={`${idPrefix}-typ-custom`} />
      )}
    </div>
  );
}

function RolesField({ roles, onToggle, idPrefix }) {
  return (
    <div className="space-y-2">
      <Label>Rollen (Mehrfachauswahl möglich)</Label>
      <div className="space-y-1.5">
        {ROLE_OPTIONS.map((r) => (
          <label key={r.value} className="flex items-center gap-2 cursor-pointer">
            <Checkbox checked={roles.includes(r.value)} onCheckedChange={() => onToggle(r.value)} data-testid={`${idPrefix}-role-${r.value}`} />
            <span className="text-sm">{r.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

export default function Users() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyCreate);
  const [resetFor, setResetFor] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [editUser, setEditUser] = useState(null);

  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const staffUsers = users.filter((u) => (u.roles && u.roles.length ? u.roles : u.role ? [u.role] : []).some((r) => STAFF_ROLES.includes(r)));

  const toggleRole = (state, setState, r) => {
    const roles = state.roles.includes(r) ? state.roles.filter((x) => x !== r) : [...state.roles, r];
    setState({ ...state, roles });
  };

  const create = async () => {
    if (!form.roles.length) return toast.error("Bitte mindestens eine Rolle wählen.");
    try {
      await api.post("/admin/users", { ...form, typ: (form.typ || "").trim() });
      toast.success("Benutzer angelegt.");
      setOpen(false);
      setForm(emptyCreate);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const reset = async () => {
    try {
      await api.post(`/admin/users/${resetFor.id}/reset-password`, { new_password: newPw });
      toast.success("Passwort zurückgesetzt.");
      setResetFor(null);
      setNewPw("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Benutzer wirklich löschen?")) return;
    await api.delete(`/admin/users/${id}`);
    toast.success("Benutzer gelöscht.");
    load();
  };

  const openEdit = (u) => setEditUser({
    id: u.id, titel: u.titel || "", vorname: u.vorname || "", nachname: u.nachname || "",
    geschlecht: u.geschlecht || "", typ: u.typ || "", email: u.email,
    roles: u.roles && u.roles.length ? u.roles : u.role ? [u.role] : [],
  });
  const saveEdit = async () => {
    if (!editUser.roles.length) return toast.error("Bitte mindestens eine Rolle wählen.");
    try {
      const { id, ...payload } = editUser;
      await api.put(`/admin/users/${id}`, { ...payload, typ: (payload.typ || "").trim() });
      toast.success("Benutzer aktualisiert.");
      setEditUser(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="px-8 py-10">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <Users2 className="text-primary" />
          <h1 className="text-3xl font-serif">User</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-button"><Plus size={16} className="mr-1" /> Benutzer anlegen</Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Neuen Benutzer anlegen</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Titel (optional)</Label><Input value={form.titel} onChange={(e) => setForm({ ...form, titel: e.target.value })} placeholder="z.B. Dr." data-testid="new-user-titel" /></div>
                <div className="space-y-2">
                  <Label>Geschlecht</Label>
                  <Select value={form.geschlecht} onValueChange={(v) => setForm({ ...form, geschlecht: v, typ: "" })}>
                    <SelectTrigger data-testid="new-user-geschlecht"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="m">männlich</SelectItem>
                      <SelectItem value="w">weiblich</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Vorname</Label><Input value={form.vorname} onChange={(e) => setForm({ ...form, vorname: e.target.value })} data-testid="new-user-vorname" /></div>
                <div className="space-y-2"><Label>Nachname</Label><Input value={form.nachname} onChange={(e) => setForm({ ...form, nachname: e.target.value })} data-testid="new-user-nachname" /></div>
              </div>
              <TypField geschlecht={form.geschlecht} value={form.typ} onChange={(v) => setForm({ ...form, typ: v })} idPrefix="new-user" />
              <div className="space-y-2"><Label>E-Mail</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" /></div>
              <div className="space-y-2"><Label>Passwort</Label><Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" /></div>
              <RolesField roles={form.roles} onToggle={(r) => toggleRole(form, setForm, r)} idPrefix="new-user" />
            </div>
            <DialogFooter><Button onClick={create} data-testid="save-user-button">Anlegen</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <p className="text-muted-foreground mb-8 text-sm">Verwaltung der Benutzer und ihrer Rollen.</p>

      <div className="bg-white border border-border rounded-lg shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead><TableHead>E-Mail</TableHead><TableHead>Typ</TableHead><TableHead>Rollen</TableHead><TableHead className="text-right">Aktionen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {staffUsers.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell>{u.name}</TableCell>
                <TableCell className="text-sm">{u.email}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{u.typ || "—"}</TableCell>
                <TableCell className="space-x-1">
                  {(u.roles && u.roles.length ? u.roles : u.role ? [u.role] : []).map((r) => (
                    <span key={r} className="text-xs px-2 py-0.5 rounded-full bg-accent text-accent-foreground">{ROLE_LABELS[r] || r}</span>
                  ))}
                </TableCell>
                <TableCell className="text-right space-x-1">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil size={16} /></Button>
                  <Button variant="ghost" size="sm" onClick={() => setResetFor(u)} data-testid={`reset-pw-${u.id}`}><KeyRound size={16} /></Button>
                  <Button variant="ghost" size="sm" onClick={() => del(u.id)} data-testid={`delete-user-${u.id}`}><Trash2 size={16} className="text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {staffUsers.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Keine Benutzer angelegt.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!resetFor} onOpenChange={(o) => !o && setResetFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Passwort zurücksetzen</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Neues Passwort für {resetFor?.email}</p>
          <Input type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Neues Passwort" data-testid="reset-new-password" />
          <DialogFooter><Button onClick={reset} data-testid="confirm-reset-button">Zurücksetzen</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Benutzer bearbeiten</DialogTitle></DialogHeader>
          {editUser && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Titel (optional)</Label><Input value={editUser.titel} onChange={(e) => setEditUser({ ...editUser, titel: e.target.value })} placeholder="z.B. Dr." data-testid="edit-user-titel" /></div>
                <div className="space-y-2">
                  <Label>Geschlecht</Label>
                  <Select value={editUser.geschlecht} onValueChange={(v) => setEditUser({ ...editUser, geschlecht: v, typ: "" })}>
                    <SelectTrigger data-testid="edit-user-geschlecht"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent><SelectItem value="m">männlich</SelectItem><SelectItem value="w">weiblich</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Vorname</Label><Input value={editUser.vorname} onChange={(e) => setEditUser({ ...editUser, vorname: e.target.value })} data-testid="edit-user-vorname" /></div>
                <div className="space-y-2"><Label>Nachname</Label><Input value={editUser.nachname} onChange={(e) => setEditUser({ ...editUser, nachname: e.target.value })} data-testid="edit-user-nachname" /></div>
              </div>
              <TypField geschlecht={editUser.geschlecht} value={editUser.typ} onChange={(v) => setEditUser({ ...editUser, typ: v })} idPrefix="edit-user" />
              <div className="space-y-2"><Label>E-Mail</Label><Input type="email" value={editUser.email} onChange={(e) => setEditUser({ ...editUser, email: e.target.value })} data-testid="edit-user-email" /></div>
              <RolesField roles={editUser.roles} onToggle={(r) => toggleRole(editUser, setEditUser, r)} idPrefix="edit-user" />
            </div>
          )}
          <DialogFooter><Button onClick={saveEdit} data-testid="save-edit-user-button">Speichern</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
